import { Fragment, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Stack,
} from '@mui/material';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';

// Icons
import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';

// Services
import proposalsServices from '../../../services/proposalsServices';
import guaranteeServices from '../../../services/guaranteeServices';

// Utils
import messages from '../../../static/messages';

// Modal
import GuaranteesSelectModal from '../../Modals/GuaranteesSelectModal';

// Drag and Drop
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';

// DnD Components
const DragHandle = SortableHandle(() => (
  <DragIndicatorIcon sx={{ cursor: 'grab', color: '#999', '&:hover': { color: '#333' } }} />
));

const SortableTableRow = SortableElement(({ children, ...props }) => (
  <TableRow {...props}>{children}</TableRow>
));

const SortableTableBody = SortableContainer(({ children }) => (
  <TableBody>{children}</TableBody>
));

const ProposalGuaranteesTable = ({ proposal, id, refresh }) => {
  const [guaranteeList, setGuaranteeList] = useState([]);
  const [selectedGuarantees, setSelectedGuarantees] = useState(proposal?.guarantees || []);
  const [selectModalOpen, setSelectModalOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const confirm = useConfirm();

  // Load guarantee list from items/guarantees
  const loadGuaranteeList = async () => {
    try {
      const response = await guaranteeServices.getAllGuarantees(0, 1000000, false);
      if (response.status === 206 || response.status === 200) {
        setGuaranteeList(response.data.data || response.data || []);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    loadGuaranteeList();
  }, []);

  useEffect(() => {
    setSelectedGuarantees(proposal?.guarantees || []);
  }, [proposal]);

  // Add multiple guarantees from modal
  const handleAddMultiple = async (selectedItems) => {
    const newGuarantees = selectedItems.map(item => ({
      id: item.id,
      description: item.description,
    }));

    const updatedGuarantees = [...selectedGuarantees, ...newGuarantees];

    try {
      const response = await proposalsServices.update(id, {
        guarantees: updatedGuarantees,
      });

      if (response.status === 200) {
        toast.success(`${newGuarantees.length} guarantee(s) added`);
        refresh();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  // Delete guarantee
  const handleDeleteGuarantee = async (guaranteeId, description) => {
    confirm({
      title: messages.confirm_message.remove,
      description: messages.confirm_message.remove_description(`"${description}"`),
    })
      .then(async () => {
        const updatedGuarantees = selectedGuarantees.filter(g => g.id !== guaranteeId);

        try {
          const response = await proposalsServices.update(id, {
            guarantees: updatedGuarantees,
          });

          if (response.status === 200) {
            toast.success('Guarantee removed');
            refresh();
          }
        } catch (error) {
          toast.error(messages.errors.error_try_again);
          console.log(error);
        }
      })
      .catch(() => {});
  };

  // Handle drag and drop reorder
  const handleSortEnd = async ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return;
    
    const reorderedGuarantees = arrayMoveImmutable(selectedGuarantees, oldIndex, newIndex);
    setSelectedGuarantees(reorderedGuarantees);

    try {
      const response = await proposalsServices.update(id, {
        guarantees: reorderedGuarantees,
      });

      if (response.status !== 200) {
        // Revert on error
        refresh();
      }
    } catch (error) {
      console.log(error);
      refresh();
    }
  };

  return (
    <Fragment>
      {/* Guarantees List with Drag & Drop */}
      {selectedGuarantees.length > 0 ? (
        <TableContainer sx={{ mb: 2, overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: isMobile ? 'unset' : 300 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: isMobile ? 30 : 40, p: isMobile ? '4px' : undefined }}></TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="center" sx={{ width: isMobile ? 50 : 80 }}></TableCell>
              </TableRow>
            </TableHead>
            <SortableTableBody
              useDragHandle
              onSortEnd={handleSortEnd}
              helperClass="sortable-row-helper"
            >
              {selectedGuarantees.map((guarantee, index) => (
                <SortableTableRow key={guarantee.id} index={index}>
                  <TableCell sx={{ width: isMobile ? 30 : 40, p: isMobile ? '4px' : undefined }}>
                    <DragHandle />
                  </TableCell>
                  <TableCell sx={{ fontSize: isMobile ? '0.85rem' : 'inherit' }}>{guarantee.description}</TableCell>
                  <TableCell align="center" sx={{ p: isMobile ? '2px' : undefined }}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteGuarantee(guarantee.id, guarantee.description)}
                    >
                      <DeleteForeverIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </SortableTableRow>
              ))}
            </SortableTableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No guarantees added yet. Click "Add Guarantees" to select from catalog.
        </Typography>
      )}

      {/* Add buttons */}
      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<PlaylistAddIcon />}
          onClick={() => setSelectModalOpen(true)}
        >
          Add Guarantees
        </Button>
      </Stack>

      {/* Select from catalog modal */}
      <GuaranteesSelectModal
        open={selectModalOpen}
        onClose={() => setSelectModalOpen(false)}
        guaranteesList={guaranteeList}
        existingItems={selectedGuarantees}
        onAdd={handleAddMultiple}
      />
    </Fragment>
  );
};

export default ProposalGuaranteesTable;
